import { inject, Injectable } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc
} from '@angular/fire/firestore';
import { from, map, Observable } from 'rxjs';

export type SchemaRuleDocument = {
  id: string;
  rule: string;
  function_name: string;
  column?: string;
  value: boolean;
  created_at?: unknown;
  updated_at?: unknown;
};

export type CreateSchemaRulePayload = {
  rule: string;
  function_name: string;
  value: boolean;
};

@Injectable({ providedIn: 'root' })
export class SchemaRulesService {
  private firestore = inject(Firestore);

  getRules$(): Observable<SchemaRuleDocument[]> {
    const q = query(collection(this.firestore, 'schema_rules'), orderBy('rule'));
    return from(getDocs(q)).pipe(
      map((snap) =>
        snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<SchemaRuleDocument, 'id'>)
        }))
      )
    );
  }

  getRuleById$(ruleId: string): Observable<SchemaRuleDocument | null> {
    const ruleRef = doc(this.firestore, 'schema_rules', ruleId);
    return from(getDoc(ruleRef)).pipe(
      map((snap) => {
        if (!snap.exists()) return null;
        return {
          id: snap.id,
          ...(snap.data() as Omit<SchemaRuleDocument, 'id'>)
        };
      })
    );
  }

  createRule$(payload: CreateSchemaRulePayload): Observable<string> {
    const rulesRef = collection(this.firestore, 'schema_rules');
    const ruleRef = doc(rulesRef);
    return from(
      setDoc(ruleRef, {
        id: ruleRef.id,
        ...payload,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      })
    ).pipe(map(() => ruleRef.id));
  }

  updateRule$(ruleId: string, payload: CreateSchemaRulePayload): Observable<void> {
    const ruleRef = doc(this.firestore, 'schema_rules', ruleId);
    return from(
      updateDoc(ruleRef, {
        id: ruleId,
        ...payload,
        updated_at: serverTimestamp()
      })
    );
  }

  deleteRule$(ruleId: string): Observable<void> {
    const ruleRef = doc(this.firestore, 'schema_rules', ruleId);
    return from(deleteDoc(ruleRef));
  }
}
